import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { APP } from '@/constants/testIds';
import { Sprout, LogOut, Menu, X } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/', end: true, label: 'Etusivu', testId: APP.navDashboard },
  { to: '/tilaukset', label: 'Tilauskirja', testId: APP.navOrders },
  { to: '/asiakkaat', label: 'Asiakkaat', testId: APP.navCustomers },
  { to: '/reitit', label: 'Reitit', testId: APP.navRoutes },
  { to: '/hinnat', label: 'Hinnat', testId: APP.navPrices },
  { to: '/laskutus', label: 'Laskutus', testId: APP.navInvoicing },
  { to: '/poimintamaarat', label: 'Poimintamäärät', testId: APP.navHarvest },
  { to: '/kylmio', label: 'Kylmiö', testId: 'nav-kylmio' },
  { to: '/raportit', label: 'Raportit', testId: 'nav-reports' },
];

function DesktopLink({ item }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testId}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-full text-sm transition-colors ${
          isActive ? 'bg-[#2A4B30] text-white' : 'text-[#6A6D66] hover:bg-[#E5E6E3] hover:text-[#1C1C1A]'
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

function MobileLink({ item, onClick }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`mob-${item.testId}`}
      className={({ isActive }) =>
        `block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
          isActive ? 'bg-[#2A4B30] text-white' : 'text-[#1C1C1A] hover:bg-[#E5E6E3]'
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === 'admin';
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-[#F9F9F6] text-[#1C1C1A]">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E6E3] safe-top safe-x">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 lg:gap-8 min-w-0">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 rounded-full bg-[#2A4B30] text-white flex items-center justify-center">
                <Sprout size={16} strokeWidth={2.2} />
              </div>
              <span className="font-heading text-lg tracking-tight hidden xs:inline sm:inline">Herne&nbsp;Tilaukset</span>
              <span className="font-heading text-lg tracking-tight xs:hidden sm:hidden">Herne</span>
            </div>
            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((it) => (
                <DesktopLink key={it.to} item={it} />
              ))}
              {isAdmin && (
                <DesktopLink item={{ to: '/kayttajat', label: 'Käyttäjät', testId: APP.navUsers }} />
              )}
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {user && typeof user === 'object' && (
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-sm font-medium">{user.name}</span>
                <span className="text-xs text-[#6A6D66] font-mono">@{user.username}</span>
              </div>
            )}
            <button
              onClick={logout}
              data-testid={APP.logout}
              className="hidden sm:flex items-center gap-1.5 text-sm text-[#6A6D66] hover:text-[#C05A3C] px-3 py-1.5 rounded-full hover:bg-[#FBEAE5] transition-colors"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">Kirjaudu ulos</span>
            </button>
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Avaa valikko"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#E5E6E3] text-[#1C1C1A]"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu-drawer">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMobile}
            aria-hidden="true"
          />
          <div className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl flex flex-col safe-top safe-bottom">
            <div className="flex items-center justify-between px-4 h-16 border-b border-[#E5E6E3]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#2A4B30] text-white flex items-center justify-center">
                  <Sprout size={16} strokeWidth={2.2} />
                </div>
                <span className="font-heading text-lg tracking-tight">Herne</span>
              </div>
              <button
                type="button"
                onClick={closeMobile}
                data-testid="mobile-menu-close"
                aria-label="Sulje valikko"
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#E5E6E3] text-[#1C1C1A]"
              >
                <X size={22} />
              </button>
            </div>
            {user && typeof user === 'object' && (
              <div className="px-4 py-3 border-b border-[#E5E6E3]">
                <p className="text-sm font-medium leading-tight">{user.name}</p>
                <p className="text-xs text-[#6A6D66] font-mono leading-tight">@{user.username}</p>
              </div>
            )}
            <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
              {NAV_ITEMS.map((it) => (
                <MobileLink key={it.to} item={it} onClick={closeMobile} />
              ))}
              {isAdmin && (
                <MobileLink
                  item={{ to: '/kayttajat', label: 'Käyttäjät', testId: APP.navUsers }}
                  onClick={closeMobile}
                />
              )}
            </nav>
            <div className="border-t border-[#E5E6E3] p-3">
              <button
                onClick={() => {
                  closeMobile();
                  logout();
                }}
                data-testid="mobile-logout"
                className="w-full flex items-center justify-center gap-2 text-sm text-[#C05A3C] px-4 py-3 rounded-xl bg-[#FBEAE5] hover:bg-[#F5D6CD] transition-colors"
              >
                <LogOut size={16} />
                Kirjaudu ulos
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 safe-x">
        <Outlet />
      </main>
    </div>
  );
}
